import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import { Star, MapPin, Clock, Calendar, ArrowLeft, ShoppingBag, Heart, Share2, Check, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useAuth, useCart } from '../context/AppContext';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ExperienceDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { addToCart } = useCart();

  const [experience, setExperience] = useState(null);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [selectedDate, setSelectedDate] = useState('');

  useEffect(() => {
    const fetchExperience = async () => {
      try {
        const res = await axios.get(`${API}/experiences/${id}`);
        setExperience(res.data);
      } catch (err) {
        toast.error('Kunde inte hämta upplevelsen');
        navigate('/upplevelser');
      } finally {
        setLoading(false);
      }
    };
    fetchExperience();
  }, [id, navigate]);

  const handleAddToCart = async () => {
    if (!user) {
      toast.error('Logga in för att lägga till i varukorgen');
      return;
    }
    setAdding(true);
    try {
      await addToCart(experience.id, 1, selectedDate);
      toast.success('Tillagd i varukorgen!');
    } catch (err) {
      toast.error('Kunde inte lägga till');
    } finally {
      setAdding(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#8B6F4E]" />
      </div>
    );
  }

  if (!experience) return null;

  return (
    <div className="min-h-screen bg-[#F9F9F7]" data-testid="experience-detail-page">
      <div className="container-main py-8">
        {/* Back button */}
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-[#8C8C8C] hover:text-[#2C3E36] mb-6 transition-colors"
          data-testid="back-button"
        >
          <ArrowLeft className="w-5 h-5" />
          Tillbaka
        </button>

        <div className="grid lg:grid-cols-2 gap-12">
          {/* Image */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            className="relative"
          >
            <img
              src={experience.image_url}
              alt={experience.name}
              className="w-full aspect-[4/3] object-cover rounded-3xl shadow-lg"
            />
            <div className="absolute top-4 right-4 flex gap-2">
              <button className="p-3 bg-white/90 backdrop-blur-sm rounded-full hover:bg-white transition-colors">
                <Heart className="w-5 h-5 text-[#2C3E36]" />
              </button>
              <button className="p-3 bg-white/90 backdrop-blur-sm rounded-full hover:bg-white transition-colors">
                <Share2 className="w-5 h-5 text-[#2C3E36]" />
              </button>
            </div>
          </motion.div>

          {/* Details */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            className="space-y-6"
          >
            {/* Category badge */}
            <span className="inline-block px-4 py-1.5 bg-[#C8A67B]/20 text-[#8B6F4E] rounded-full text-sm font-medium">
              {experience.category === 'spa' && 'Spa & Wellness'}
              {experience.category === 'massage' && 'Massage'}
              {experience.category === 'biluthyrning' && 'Biluthyrning'}
              {experience.category === 'barn' && 'Barn & Familj'}
              {experience.category === 'mat' && 'Mat & Dryck'}
            </span>

            <h1 className="font-['Avenir_Next'] text-4xl md:text-5xl text-[#1A1A1A]" data-testid="experience-name">
              {experience.name}
            </h1>

            {/* Rating */}
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-1">
                {[...Array(5)].map((_, i) => (
                  <Star
                    key={i}
                    className={`w-5 h-5 ${i < Math.floor(experience.rating) ? 'text-[#C8A67B] fill-current' : 'text-[#E8E8E4]'}`}
                  />
                ))}
                <span className="ml-2 font-medium text-[#1A1A1A]">{experience.rating}</span>
              </div>
              <span className="text-[#8C8C8C]">{experience.reviews_count} recensioner</span>
            </div>

            {/* Meta info */}
            <div className="flex flex-wrap gap-6 text-[#595959]">
              <div className="flex items-center gap-2">
                <MapPin className="w-5 h-5 text-[#8B6F4E]" />
                <span>{experience.location}</span>
              </div>
              <div className="flex items-center gap-2">
                <Clock className="w-5 h-5 text-[#8B6F4E]" />
                <span>{experience.duration}</span>
              </div>
            </div>

            {/* Description */}
            <p className="text-[#595959] text-lg leading-relaxed">
              {experience.description}
            </p>

            {/* Date picker */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-[#1A1A1A] flex items-center gap-2">
                <Calendar className="w-4 h-4" />
                Välj datum (valfritt)
              </label>
              <input
                type="date"
                value={selectedDate}
                onChange={(e) => setSelectedDate(e.target.value)}
                min={new Date().toISOString().split('T')[0]}
                className="w-full h-12 px-4 rounded-xl border border-[#8B6F4E]/20 focus:border-[#8B6F4E] focus:outline-none"
                data-testid="date-picker"
              />
            </div>

            {/* Price & CTA */}
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-6 pt-4 border-t border-[#8B6F4E]/10">
              <div>
                <span className="text-[#8C8C8C] text-sm">Pris</span>
                <p className="font-['Avenir_Next'] text-4xl text-[#8B6F4E]" data-testid="experience-price">
                  {experience.price.toLocaleString('sv-SE')} kr
                </p>
              </div>
              <Button
                onClick={handleAddToCart}
                disabled={adding}
                className="btn-primary h-14 px-8 text-base flex-1 sm:flex-initial"
                data-testid="add-to-cart-button"
              >
                {adding ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <>
                    <ShoppingBag className="w-5 h-5 mr-2" />
                    Lägg i varukorg
                  </>
                )}
              </Button>
            </div>

            {/* Features */}
            <div className="bg-white rounded-2xl p-6 space-y-4">
              <h3 className="font-medium text-[#1A1A1A]">Inkluderat</h3>
              <ul className="space-y-3">
                {['Professionell personal', 'Högkvalitativ utrustning', 'Bekväm miljö', 'Flexibel avbokning'].map((item, i) => (
                  <li key={i} className="flex items-center gap-3 text-[#595959]">
                    <Check className="w-5 h-5 text-[#4A6C48]" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>

            {/* Klarna info */}
            <div className="flex items-center gap-3 p-4 bg-[#FFB3C7]/20 rounded-xl">
              <span className="klarna-badge">Klarna</span>
              <span className="text-sm text-[#595959]">Betala nu eller dela upp betalningen</span>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
}
